import type { MouseEvent, FC } from 'react';
import type { SalesValues } from 'shared/types/Sales';

import { useMemo, useRef, useCallback, useState } from 'react';
import tinycolor from 'tinycolor2';
import styled from 'styled-components';

import { sanitizeRichText } from 'shared/helpers/utils';
import { defaultWebValue, isBoundingClientRectZoomed } from 'shared/helpers/website';

import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import { ZOOM_SIZE } from 'shared/helpers/static';
import ButtonActionModal from 'shared/components/Common/ButtonActionModal';
import ButtonSettingIcon from './ButtonSettingIcon';

interface StyledButtonProps {
  $isEditable?: boolean;
  hidden?: boolean;
}

const ButtonInner = styled.div`
  position: relative;
  width: fit-content;

  @media screen and (max-width: 575px) {
    width: 100%;
    padding: 0 2.5%;
  }
`;

const StyledButton = styled.a<StyledButtonProps>`
  all: unset;
  min-width: 200px;
  min-height: 10px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  padding: 24px 40px;
  border-radius: 40px;
  text-align: center;
  transition:
    transform 0.2s ease 0s,
    box-shadow 0.2s ease 0s;
  cursor: pointer;
  display: block;
  max-width: 600px;

  ${(props) =>
    props.$isEditable
      ? 'cursor: text; word-break: break-word;'
      : `
  &:hover {
    transform: translateY(-3px);
    box-shadow: rgba(0, 0, 0, 0.2) 0px 1rem 2rem;
    transition:
      transform 0.2s ease 0s,
      box-shadow 0.2s ease 0s;
  }
  `}

  @media (max-width: 991px) {
    padding: 17px 28px;
  }

  @media (max-width: 768px) {
    &:hover {
      transform: translateY(0px);
      box-shadow: rgba(0, 0, 0, 0) 0px 0rem 0rem;
    }
  }

  @media (max-width: 575px) {
    width: -webkit-fill-available;
    padding: 25px 20px;
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
  }

  @media (max-width: 500px) {
    padding: 19px 32px;
  }

  @media (max-width: 380px) {
    padding: 16px 27px;
  }

  @media (max-width: 340px) {
    padding: 16px 25px;
  }

  @media (max-width: 320px) {
    font-size: 11px;
    padding: 15px 23px;
  }

  @media (max-width: 300px) {
    font-size: 10px;
    padding: 15px 22px;
  }

  @media (max-width: 270px) {
    font-size: 8px;
    padding: 15px 22px;
  }

  @media (max-width: 250px) {
    font-size: 7px;
    padding: 15px 22px;
  }

  @media (max-width: 230px) {
    font-size: 6px;
    padding: 15px 22px;
  }

  & p {
    font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
    display: inline;
  }
`;

export interface ButtonProps {
  id?: string;
  button?: SalesValues;
  meta?: Record<string, string>;
  outerOnClick?: () => void;
  to?: string;
  offset?: number;
  benefitIndex?: number;
  isBotPosition?: boolean;
  className?: string;
}

export const Button: FC<ButtonProps> = (props) => {
  const {
    id,
    button = defaultWebValue,
    meta,
    outerOnClick,
    to,
    offset = 0,
    benefitIndex,
    isBotPosition,
    className // important for responsive versions
  } = props;
  const buttonRef = useRef<HTMLAnchorElement>(null);

  const [isButtonActionModalOpen, setIsButtonActionModalOpen] = useState(false);
  const [showLinkIcon, setShowLinkIcon] = useState(false);

  const textColor = useMemo(() => {
    return tinycolor(button.color).isDark() ? '#FFFFFF' : '#000000';
  }, [button.color]);

  const { updateActiveElement, resetActiveElement, editable } = useWebsiteEditor();
  const isEditable = process.env['APP_TYPE'] === 'dashboard';

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (updateActiveElement) {
        updateActiveElement(e);
      }
      if (isEditable) {
        outerOnClick?.();
        return;
      }

      if (to) {
        e.preventDefault();

        const targetElement = document.getElementById(to);

        if (targetElement) {
          requestAnimationFrame(() => {
            const rect = targetElement.getBoundingClientRect();
            const isZoomed = isBoundingClientRectZoomed(targetElement);

            let adjustedTop;
            if (!isZoomed) {
              // Apply zoom scaling and calculate offset
              const scaledTop = rect.top * ZOOM_SIZE;
              adjustedTop = scaledTop + window.pageYOffset;
            } else {
              adjustedTop = rect.top + window.pageYOffset;
            }

            const targetPosition = adjustedTop + (offset || 0);

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          });
        }
      }
    },
    [to, offset, outerOnClick, updateActiveElement, isEditable]
  );

  return (
    <ButtonInner
      onMouseEnter={() => setShowLinkIcon(true)}
      onMouseLeave={() => setShowLinkIcon(isButtonActionModalOpen)}>
      <StyledButton
        id={id ?? button?.id}
        className={className}
        aria-label={button.val ? `Action: ${button.val}` : 'Action button'}
        hidden={button.hidden}
        style={{ backgroundColor: button.color, color: textColor }}
        onClick={handleClick}
        onBlur={resetActiveElement}
        dangerouslySetInnerHTML={{
          __html: sanitizeRichText(button.val || 'Add Text Here')
        }}
        data-accessor-parent={meta?.['accessorKey']}
        data-accessorkey="button"
        $isEditable={isEditable}
        ref={buttonRef}
        {...(process.env['APP_TYPE'] === 'dashboard' ? { as: 'p' } : { as: 'a' })}
        {...(benefitIndex ? { 'data-benefits': true, 'data-pindex': benefitIndex } : {})}
      />
      {isEditable && editable && showLinkIcon ? (
        <ButtonSettingIcon
          setIsButtonActionModalOpen={setIsButtonActionModalOpen}
          setShowLinkIcon={setShowLinkIcon}
        />
      ) : null}
      {isButtonActionModalOpen && isEditable ? (
        <ButtonActionModal
          onClose={() => {
            setIsButtonActionModalOpen(false);
            setShowLinkIcon(false);
          }}
          isBotPosition={isBotPosition}
          meta={meta ?? {}}
          benefitIndex={benefitIndex}
        />
      ) : null}
    </ButtonInner>
  );
};

Button.displayName = 'SalesButton';
